import { X } from "lucide-react";
import { clsx } from "clsx";
import { Dialog } from "radix-ui";
import React, { PropsWithChildren, useState } from "react";

import { PortalContainerContext } from "@/design-system/portalContainer";
import { twMerge } from "@/design-system/twMerge";

export type DrawerAccent = "none" | "critical" | "warning" | "success" | "info";

const accentBarClasses: Record<Exclude<DrawerAccent, "none">, string> = {
  critical: "bg-status-error",
  warning: "bg-status-warning",
  success: "bg-status-success",
  info: "bg-status-info",
};

interface DrawerProps {
  title: string;
  eyebrow?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** Icon shown in a 30×30 tile to the left of the title (compact headers). */
  titleLeading?: React.ReactNode;
  /** Tighter header padding and title size for chat-style panels. */
  headerDensity?: "default" | "compact";
  accent?: DrawerAccent;
  accentColor?: string;
  onOpenChange: (open: boolean) => void;
  /**
   * Handles the header close button separately from Escape and outside
   * clicks, for drawers where those dismissals mean different things (e.g.
   * minimize vs. discard). Defaults to `onOpenChange(false)`.
   */
  onCloseButtonClick?: () => void;
  /** Accessible name of the header close button. Defaults to "Close". */
  closeButtonLabel?: string;
  /** Extra header controls rendered before the close button. */
  headerActions?: React.ReactNode;
  open: boolean;
  children: React.ReactNode;
  placement: "bottom" | "right";
  className?: string;
  titleClassName?: string;
  overlayProps?: Partial<Dialog.DialogOverlayProps>;
  onOpenAutoFocus?: Dialog.DialogContentProps["onOpenAutoFocus"];
}

export function Drawer({
  title,
  eyebrow,
  subtitle,
  titleLeading,
  headerDensity = "default",
  accent = "none",
  accentColor,
  children,
  onOpenChange,
  onCloseButtonClick,
  closeButtonLabel = "Close",
  headerActions,
  placement,
  className,
  titleClassName,
  overlayProps,
  onOpenAutoFocus,
  ...rest
}: DrawerProps) {
  const [content, setContent] = useState<HTMLDivElement | null>(null);

  return (
    <Dialog.Root {...rest}>
      <Dialog.Portal>
        <Dialog.Overlay
          {...overlayProps}
          className={twMerge(
            clsx(
              "data-[state=open]:animate-fadeIn data-[state=closed]:animate-fadeOut z-overlay fixed top-0 left-0 flex h-full w-full flex-col bg-black/50 pt-12",
              overlayProps?.className,
            ),
          )}
        >
          <Dialog.Content
            ref={setContent}
            onEscapeKeyDown={(event) => {
              event.preventDefault();
              if (content) {
                handleEscape(event, content, () => onOpenChange(false));
              }
            }}
            onPointerDownOutside={() => onOpenChange(false)}
            onOpenAutoFocus={onOpenAutoFocus}
            className={clsx(
              "bg-surface-default flex min-h-0 flex-1 flex-col overflow-hidden outline-none",
              placement === "bottom" &&
                "data-[state=open]:animate-slideInUp data-[state=closed]:animate-slideOutDown rounded-t-lg",
              placement === "right" &&
                "data-[state=open]:animate-slideInFromRight data-[state=closed]:animate-slideOutToRight fixed top-0 right-0 h-full w-96 max-w-[90vw]",
              className,
            )}
          >
            <div
              className={clsx(
                headerDensity === "compact"
                  ? "flex flex-0 items-center justify-between gap-2.5 border-b px-5 py-[18px]"
                  : "border-borderSubtle flex flex-0 justify-between gap-4 border-b px-8 py-6",
                headerDensity !== "compact" &&
                  (eyebrow || subtitle ? "items-start" : "items-center"),
                titleClassName,
              )}
            >
              <div
                className={clsx(
                  "flex min-w-0 flex-1",
                  headerDensity === "compact"
                    ? "items-center gap-2.5"
                    : "items-stretch gap-4",
                )}
              >
                {(accent !== "none" || accentColor) && (
                  <div
                    className={clsx(
                      "w-1 shrink-0 self-stretch",
                      !accentColor &&
                        accent !== "none" &&
                        accentBarClasses[accent],
                    )}
                    style={
                      accentColor ? { backgroundColor: accentColor } : undefined
                    }
                    aria-hidden="true"
                  />
                )}
                {titleLeading && (
                  <div
                    className="flex h-[30px] w-[30px] shrink-0 items-center justify-center rounded-lg bg-white/12"
                    aria-hidden
                  >
                    {titleLeading}
                  </div>
                )}
                <div className="flex min-w-0 flex-col gap-0.5">
                  {eyebrow && (
                    <span className="text-eyebrow-xs text-textSubtle truncate">
                      {eyebrow}
                    </span>
                  )}
                  <Dialog.Title
                    className={clsx(
                      headerDensity === "compact"
                        ? "text-textDefault text-[15px] leading-5 font-semibold"
                        : "text-heading-xl text-textDefault",
                    )}
                  >
                    {title}
                  </Dialog.Title>
                  {subtitle && (
                    <span
                      className={clsx(
                        "truncate",
                        headerDensity === "compact"
                          ? "text-textSubtle text-xs"
                          : "text-body-sm text-textSubtle",
                      )}
                    >
                      {subtitle}
                    </span>
                  )}
                </div>
              </div>
              {headerActions}
              <Dialog.Close
                aria-label={closeButtonLabel}
                onClick={() =>
                  onCloseButtonClick
                    ? onCloseButtonClick()
                    : onOpenChange(false)
                }
                className={clsx(
                  "shrink-0 cursor-pointer transition-colors",
                  headerDensity === "compact"
                    ? "text-textSubtle hover:text-textDefault flex h-7 w-7 items-center justify-center rounded-md"
                    : "text-textSubtle hover:text-textDefault",
                )}
              >
                <X
                  className={
                    headerDensity === "compact" ? "h-4 w-4" : undefined
                  }
                />
              </Dialog.Close>
            </div>
            <PortalContainerContext.Provider value={content}>
              {children}
            </PortalContainerContext.Provider>
          </Dialog.Content>
        </Dialog.Overlay>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

const OPEN_POPUP_SELECTOR =
  '[role="combobox"][aria-expanded="true"], [aria-haspopup]:not([aria-haspopup="false"])[aria-expanded="true"]';

const NON_TEXT_INPUT_TYPES = new Set([
  "button",
  "checkbox",
  "color",
  "file",
  "image",
  "radio",
  "range",
  "reset",
  "submit",
]);

function isTextField(element: Element) {
  if (element.getAttribute("role") === "combobox") return false;
  return (
    element instanceof HTMLTextAreaElement ||
    (element instanceof HTMLInputElement &&
      !NON_TEXT_INPUT_TYPES.has(element.type)) ||
    (element instanceof HTMLElement && element.isContentEditable)
  );
}

/**
 * Radix sees Escape on the document before the focused element does, so a
 * popup inside the drawer that isn't a Radix layer (react-select, Material UI
 * menus, custom comboboxes) would lose the key to the drawer. The drawer only
 * dismisses once nothing inside has claimed the key, and the first Escape in
 * a text field just leaves the field.
 */
function handleEscape(
  event: KeyboardEvent,
  content: HTMLElement,
  dismiss: () => void,
) {
  const target = event.target;
  if (event.isComposing || !(target instanceof Element)) return;
  const doc = content.ownerDocument;
  const ownedByAnotherSurface =
    target !== doc.body && !content.contains(target);
  if (ownedByAnotherSurface || target.closest(OPEN_POPUP_SELECTOR)) return;

  let unclaimed = false;
  const markUnclaimed = (bubbled: Event) => {
    if (bubbled === event) unclaimed = true;
  };
  doc.addEventListener("keydown", markUnclaimed);
  window.setTimeout(() => {
    doc.removeEventListener("keydown", markUnclaimed);
    if (!unclaimed || !content.isConnected) return;
    if (!isTextField(target)) {
      dismiss();
    } else if (doc.activeElement === target) {
      content.focus();
    }
  });
}

export function DrawerContent({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={twMerge(
        clsx("min-h-0 flex-1 overflow-auto px-8 py-10", className),
      )}
    >
      {children}
    </div>
  );
}

export function DrawerCtaSection({
  children,
  className,
}: PropsWithChildren<{ className?: string }>) {
  return (
    <div
      className={twMerge(
        clsx("border-borderSubtle flex-0 border-t px-8 py-4", className),
      )}
    >
      {children}
    </div>
  );
}
